import { Dispatch, memo, SetStateAction, useMemo, useState, useEffect, useContext } from 'react';
import _, { debounce } from 'lodash';
import {
  RubricAnnotation,
  RubricField,
  TextCollectionFieldType,
} from '@scale/shared/types/params/textcollection';
import { CardAlert } from 'react-scale/components/scalers/tasks/ThreadedInteraction/TempComponents';
import { Card } from 'react-scale/components/scalers/tasks/ThreadedInteraction/Card';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { CandidateMessageWithId } from '@scale/shared/types/chat-completion';
import produce from 'immer';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import {
  filterOutStaleCriteriaRatings,
  reorderCriteriaRatings,
} from '@scale/shared/textcollection/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectMenu } from '@scale/scaleui';
import {
  Button,
  TextArea,
  FormField,
  Flex,
  Box,
  Callout,
  Icon,
  TextField,
} from '@scale/scaleui-radix';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import AttachmentGroup from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/AttachmentGroup';
import useMountTime from 'react-scale/hooks/useMountTime';
import useMultimodalImageService from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMultimodalImageService';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';

interface RubricFieldProps {
  field: RubricField;
  allResponses: Record<string, TextCollectionResponse>;
  setAllResponses: (value: Record<string, TextCollectionResponse>) => void;
  handleSubmit: (value: Record<string, TextCollectionResponse>) => Promise<boolean>;
  candidates: CandidateMessageWithId[];
  setDoesRubricNeedSaving: Dispatch<SetStateAction<boolean>>;
  responseIndex: number;
  readOnly?: boolean;
}

/**
 * This component uses SelectMenu from scaleui instead of Select from scaleui-radix
 * due to performance issues with the latter.
 *
 * https://linear.app/scale-epd/issue/CHAT-1687/rubrics-section-really-slow
 */
const RubricCriteriasFieldSection = ({
  field,
  allResponses,
  setAllResponses,
  handleSubmit,
  candidates,
  readOnly,
  setDoesRubricNeedSaving,
  responseIndex,
}: RubricFieldProps) => {
  const fieldId = field.field_id;
  const annotation = allResponses[Object.keys(allResponses)[0]]?.annotations?.[fieldId];
  const initialValues = useMemo(() => {
    const rubricAnnotation = annotation?.response[0] as RubricAnnotation | undefined;
    return { criterias: rubricAnnotation?.criterias ?? [] };
  }, [annotation]);
  const [lastSavedCriterias, setLastSavedCriterias] = useState(initialValues);
  const [formikValue, setFormikValue] = useState(initialValues);
  const config = useChatStoreExtract(state => state.config);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

  const taskContext = useContext(ChatTaskContext);
  const startTime = useMountTime();
  const multimodalImageServiceHook = useMultimodalImageService({
    projectId: taskContext.projectId,
    assignmentId: taskContext.assignmentId,
    configVariables: config.templateVariables ?? {},
    reviewLevel,
    startTime,
  });

  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );

  const debMarkStepAsIncompletedByIndex = useMemo(() => {
    return debounce(markStepAsIncompletedByIndex, 1000);
  }, [markStepAsIncompletedByIndex]);

  useEffect(() => {
    const rubricAnnotations = Object.values(allResponses).map(response => {
      const rubricAnnotation = response.annotations[fieldId]?.response[0] as RubricAnnotation;
      return rubricAnnotation?.criterias ?? [];
    });
    for (const rubricAnnotation of rubricAnnotations) {
      if (!_.isEqual(rubricAnnotation, formikValue?.criterias ?? [])) {
        setDoesRubricNeedSaving(true);
        return;
      }
    }
    setDoesRubricNeedSaving(false);
  }, [formikValue, allResponses, fieldId, setDoesRubricNeedSaving]);

  const validationSchema = Yup.object().shape({
    criterias: Yup.array()
      .of(
        Yup.object().shape({
          criteria: Yup.string()
            .required('Criteria is required')
            .min(1, 'Criteria cannot be empty'),
          category: Yup.string().required('Category is required'),
          attributes: Yup.object().shape({
            ...(field.attributes?.length
              ? field.attributes?.reduce(
                  (acc, attr) => ({
                    ...acc,
                    [attr.value]: Yup.string().required(`${attr.label} is required`),
                  }),
                  {},
                )
              : {}),
          }),
          weight: field.enableWeightedScore
            ? Yup.number()
                .min(-100)
                .max(100)
                .notOneOf([0], 'Weight cannot be 0')
                .required('Weight is required')
            : Yup.number().optional(),
          attachments: field.enableAttachments
            ? Yup.array()
                .of(Yup.mixed())
                .min(
                  field.minAttachments ?? 0,
                  `Must have at least ${field.minAttachments} attachments`,
                )
                .max(
                  field.maxAttachments ?? Number.MAX_SAFE_INTEGER,
                  `Cannot have more than ${field.maxAttachments} attachments`,
                )
            : Yup.array().optional(),
        }),
      )
      .test(
        'at-least-one-weight-positive',
        'At least one weight must be positive',
        function (criterias = []) {
          if (field.enableWeightedScore) {
            const positiveWeights = criterias.filter(c => c.weight && c.weight > 0);
            return positiveWeights.length > 0;
          }
          return true;
        },
      )
      .test('unique-criteria', 'Duplicate criteria', function (criterias = []) {
        const criteriaValues = criterias.filter(c => c?.criteria).map(c => c.criteria);
        const uniqueCriterias = new Set();
        const duplicates = new Set();

        criteriaValues.forEach(criteria => {
          if (uniqueCriterias.has(criteria)) {
            duplicates.add(criteria);
          } else {
            uniqueCriterias.add(criteria);
          }
        });

        if (duplicates.size > 0) {
          return this.createError({
            message: `Duplicate criteria: ${[...duplicates].join(', ')}`,
          });
        }

        return true;
      }),
  });

  return (
    <CardAlert
      message={
        !_.isEqual(formikValue, initialValues) ? 'Please save the section below.' : undefined
      }
    >
      <Card title={field.title} subtitle={field.instruction}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange
          onSubmit={async values => {
            const newResponses = produce(allResponses, draft => {
              candidates.forEach(candidate => {
                if (!draft[candidate.id].annotations[fieldId]) {
                  draft[candidate.id].annotations[fieldId] = {
                    type: TextCollectionFieldType.Rubric,
                    field_id: fieldId,
                    response: [{ criterias: [], scores: [], criteriaRatings: [] }],
                  };
                }
                const field = draft[candidate.id].annotations[fieldId];
                if (field && field.type === TextCollectionFieldType.Rubric) {
                  if (!field.response) {
                    field.response = [{ criterias: [], scores: [], criteriaRatings: [] }];
                  }
                  field.response[0].criterias = values.criterias;
                  field.response[0].criteriaRatings = filterOutStaleCriteriaRatings(
                    values.criterias,
                    field.response[0].criteriaRatings,
                  );
                  field.response[0].criteriaRatings = reorderCriteriaRatings(
                    values.criterias,
                    field.response[0].criteriaRatings,
                  );
                }
              });
            });
            setAllResponses(newResponses);
            const isValid = await handleSubmit(newResponses);
            if (isValid) {
              setLastSavedCriterias(values);
              setDoesRubricNeedSaving(false);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, setValues }) => (
            <Form>
              <Flex direction="column" gap="4">
                {errors.criterias && typeof errors.criterias === 'string' && (
                  <Callout.Root color="error">
                    <Callout.Icon>
                      <Icon icon={faCircleInfo} label="info" />
                    </Callout.Icon>
                    <Callout.Text>{errors.criterias}</Callout.Text>
                  </Callout.Root>
                )}

                <FormField.Root size="3">
                  <FormField.Label>Rubric Design</FormField.Label>
                  <Box>
                    <FieldArray name="criterias">
                      {({ push, remove }) => (
                        <>
                          <table className="w-full border-collapse table-fixed">
                            <colgroup>
                              <col className="w-16" />
                              <col className="w-[60%]" />
                              <col className="w-[30%]" />
                              {field.attributes?.map(attr => (
                                <col key={attr.value} className="w-[30%]" />
                              ))}
                              {field.enableWeightedScore && <col className="w-[30%]" />}
                              {field.enableAttachments && <col className="w-[60%]" />}
                              <col className="w-16" />
                            </colgroup>
                            <thead>
                              <tr className="bg-neutral-50">
                                <th className="p-4 text-left w-16 border border-neutral-200">#</th>
                                <th className="p-4 text-left border border-neutral-200">
                                  Criteria
                                </th>
                                <th className="p-4 text-left border border-neutral-200">
                                  Category
                                </th>
                                {field.attributes?.map(attr => (
                                  <th
                                    key={attr.value}
                                    className="p-4 text-left border border-neutral-200"
                                  >
                                    {attr.label}
                                  </th>
                                ))}
                                {field.enableWeightedScore && (
                                  <th className="p-4 w-16 border border-neutral-200">Weight</th>
                                )}
                                {field.enableAttachments && (
                                  <th className="p-4 w-16 border border-neutral-200">
                                    Attachments
                                  </th>
                                )}
                                <th className="p-4 w-16 border border-neutral-200"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {values.criterias.map((criteria, index) => (
                                <tr key={index} className="border-b">
                                  <td className="p-4 border border-neutral-200">{index + 1}</td>
                                  <td className="p-4 border border-neutral-200">
                                    <TextArea
                                      className="w-full p-2 border rounded"
                                      resize="vertical"
                                      placeholder="Write a criterion that a response must/should/can exhibit to be perfect response. Ensure this can be assessed in a True or False manner."
                                      value={criteria?.criteria}
                                      disabled={!!readOnly}
                                      onChange={e => {
                                        setFieldValue(
                                          `criterias.${index}.criteria`,
                                          e.target.value,
                                        );
                                        setFormikValue({
                                          ...values,
                                          criterias: values.criterias.map((criteria, i) =>
                                            i === index
                                              ? { ...criteria, criteria: e.target.value }
                                              : criteria,
                                          ),
                                        });
                                        setDoesRubricNeedSaving(true);
                                        debMarkStepAsIncompletedByIndex(responseIndex);
                                      }}
                                    />
                                    {touched.criterias?.[index]?.criteria &&
                                      _.get(errors, `criterias.${index}.criteria`) && (
                                        <div className="text-danger-500 text-sm mt-1">
                                          {_.get(errors, `criterias.${index}.criteria`)}
                                        </div>
                                      )}
                                  </td>
                                  <td className="p-4 border border-neutral-200">
                                    <SelectMenu
                                      options={
                                        field.categories?.map(category => ({
                                          id: category.value,
                                          name: category.label,
                                        })) ?? [
                                          {
                                            id: 'subjective',
                                            name: 'No, there is some subjectivity in assessing if the response meets this criteria, but more than 75% of people would arrive at the same assessment.',
                                          },
                                          {
                                            id: 'objective',
                                            name: 'Yes, this criteria is specific, well-defined, and we can clearly determine if the response meets the criteria.',
                                          },
                                        ]
                                      }
                                      selectedOptionId={criteria?.category}
                                      setSelected={value => {
                                        setFieldValue(`criterias.${index}.category`, value);
                                        setFormikValue({
                                          ...values,
                                          criterias: values.criterias.map((c, i) =>
                                            i === index ? { ...c, category: value } : c,
                                          ),
                                        });
                                        setDoesRubricNeedSaving(true);
                                        debMarkStepAsIncompletedByIndex(responseIndex);
                                      }}
                                    />
                                    {touched.criterias?.[index]?.category &&
                                      _.get(errors, `criterias.${index}.category`) && (
                                        <div className="text-danger-500 text-sm mt-1">
                                          {_.get(errors, `criterias.${index}.category`)}
                                        </div>
                                      )}
                                  </td>

                                  {field.attributes?.map(attr => {
                                    const attributeCategories = attr.categories || [];
                                    const attributeKey = attr.value;

                                    return (
                                      <td
                                        key={attributeKey}
                                        className="p-4 border border-neutral-200"
                                      >
                                        <SelectMenu
                                          options={attributeCategories.map(category => ({
                                            id: category.value,
                                            name: category.label,
                                          }))}
                                          selectedOptionId={criteria?.attributes?.[attributeKey]}
                                          disabled={!!readOnly}
                                          setSelected={value => {
                                            setFieldValue(
                                              `criterias.${index}.attributes.${attributeKey}`,
                                              value,
                                            );

                                            setFormikValue({
                                              ...values,
                                              criterias: values.criterias.map((c, i) =>
                                                i === index
                                                  ? {
                                                      ...c,
                                                      attributes: {
                                                        ...c?.attributes,
                                                        [attributeKey]: value,
                                                      },
                                                    }
                                                  : c,
                                              ),
                                            });
                                            setDoesRubricNeedSaving(true);
                                            debMarkStepAsIncompletedByIndex(responseIndex);
                                          }}
                                        />
                                        {_.get(
                                          errors,
                                          `criterias.${index}.attributes.${attributeKey}`,
                                        ) && (
                                          <div className="text-danger-500 text-sm mt-1">
                                            {_.get(
                                              errors,
                                              `criterias.${index}.attributes.${attributeKey}`,
                                            )}
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}

                                  {field.enableWeightedScore && (
                                    <td className="p-4 border border-neutral-200">
                                      <div className="flex items-center gap-2">
                                        <TextField.Root
                                          type="number"
                                          value={criteria?.weight}
                                          disabled={!!readOnly}
                                          onChange={e => {
                                            const value =
                                              e.target.value === ''
                                                ? undefined
                                                : Number(e.target.value);
                                            setFieldValue(`criterias.${index}.weight`, value);
                                            setFormikValue({
                                              ...values,
                                              criterias: values.criterias.map((c, i) =>
                                                i === index ? { ...c, weight: value } : c,
                                              ),
                                            });
                                            setDoesRubricNeedSaving(true);
                                            debMarkStepAsIncompletedByIndex(responseIndex);
                                          }}
                                        />
                                      </div>
                                      {_.get(errors, `criterias.${index}.weight`) && (
                                        <div className="text-danger-500 text-sm mt-1">
                                          {_.get(errors, `criterias.${index}.weight`)}
                                        </div>
                                      )}
                                    </td>
                                  )}

                                  {field.enableAttachments && (
                                    <td className="p-4 border border-neutral-200">
                                      <div className="flex flex-col gap-2">
                                        <AttachmentGroup
                                          attachments={criteria?.attachments ?? []}
                                          setAttachments={newAttachments => {
                                            const updatedValues = {
                                              ...values,
                                              criterias: values.criterias.map((c, i) =>
                                                i === index
                                                  ? {
                                                      ...c,
                                                      attachments: newAttachments,
                                                    }
                                                  : c,
                                              ),
                                            };
                                            setValues(updatedValues);
                                            setFormikValue(updatedValues);
                                            setDoesRubricNeedSaving(true);
                                            debMarkStepAsIncompletedByIndex(responseIndex);
                                          }}
                                          multimodalImageServiceHook={multimodalImageServiceHook}
                                          enableAttachments={field.enableAttachments}
                                          readOnly={readOnly}
                                        />
                                        {_.get(errors, `criterias.${index}.attachments`) && (
                                          <div className="text-danger-500 text-sm mt-1">
                                            {_.get(errors, `criterias.${index}.attachments`)}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  )}

                                  <td className="p-4 border border-neutral-200">
                                    <Button
                                      color="gray"
                                      variant="outline"
                                      type="button"
                                      disabled={!!readOnly}
                                      onClick={() => {
                                        remove(index);
                                        setFormikValue({
                                          ...values,
                                          criterias: values.criterias.filter((_, i) => i !== index),
                                        });
                                        setDoesRubricNeedSaving(true);
                                        debMarkStepAsIncompletedByIndex(responseIndex);
                                      }}
                                    >
                                      <FontAwesomeIcon icon="trash" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <Button
                            className="mt-4"
                            color="gray"
                            type="button"
                            variant="outline"
                            onClick={() => {
                              push({
                                criteria: '',
                                category: '',
                                attributes: {},
                                attachments: field.enableAttachments ? [] : undefined,
                              });

                              setFormikValue({
                                ...values,
                                criterias: [
                                  ...values.criterias,
                                  {
                                    criteria: '',
                                    category: '',
                                    attributes: {},
                                    weight: undefined,
                                    attachments: field.enableAttachments ? [] : undefined,
                                  },
                                ],
                              });
                              setDoesRubricNeedSaving(true);
                              debMarkStepAsIncompletedByIndex(responseIndex);
                            }}
                            disabled={!!readOnly}
                          >
                            <FontAwesomeIcon icon="plus" />
                            <span className="ml-2">Add Item</span>
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </Box>
                </FormField.Root>

                <Flex justify="end">
                  <Button
                    type="submit"
                    disabled={
                      _.isEqual(lastSavedCriterias, values) ||
                      Object.keys(errors).length > 0 ||
                      readOnly
                    }
                  >
                    Save Changes
                  </Button>
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      </Card>
      <br />
    </CardAlert>
  );
};

export default memo(RubricCriteriasFieldSection);
